import React from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeClasses } from '../providers/ThemeProvider';
import { useTheme } from '../providers/ThemeProvider';
import { ChartDataService } from '../services/ChartDataService';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Helper functions to handle date parsing/formatting without timezone issues
const parseLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // month is 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility for quick ranges
const quickRanges = [
  { label: 'today', range: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { startDate: today, endDate: today };
  } },
  { label: 'yesterday', range: () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return { startDate: yesterday, endDate: yesterday };
  } },
  { label: 'lastWeek', range: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(today.getDate() - 7);
    return { startDate: start, endDate: today };
  } },
  { label: 'thisMonth', range: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    return { startDate: start, endDate: today };
  } },
  { label: 'lastThreeMonths', range: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setMonth(today.getMonth() - 3);
    start.setHours(0, 0, 0, 0);
    return { startDate: start, endDate: today };
  } },
  { label: 'allTime', range: () => {
    const today = new Date();
    const startDate = new Date(2000, 0, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    endDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  } },
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function Calendar({
  selected,
  onSelect,
  month,
  year,
  setMonth,
  setYear,
  minDate,
  maxDate,
  rangeStart,
  rangeEnd,
  themeClasses,
  currentTheme,
  availableDates
}: {
  selected: Date | null;
  onSelect: (date: Date) => void;
  month: number;
  year: number;
  setMonth: (m: number) => void;
  setYear: (y: number) => void;
  minDate: Date;
  maxDate: Date;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  themeClasses: any;
  currentTheme: any;
  availableDates?: Set<string>;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = new Date(year, month, 1).getDay();
  // Use today as a static value for the session
  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const weeks: (Date | null)[][] = [];
  let week: (Date | null)[] = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    week.push(date);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) weeks.push([...week, ...Array(7 - week.length).fill(null)]);

  // Month/year dropdowns
  const months = Array.from({ length: 12 }, (_, i) => i);
  const years = Array.from({ length: maxDate.getFullYear() - minDate.getFullYear() + 1 }, (_, i) => minDate.getFullYear() + i);

  return (
    <div className={`flex flex-col items-center w-full font-['IBM Plex Sans',sans-serif] ${themeClasses.textPrimary} text-sm sm:text-base`}>
      <div className="flex items-center gap-1 sm:gap-2 mb-3 sm:mb-4 w-full justify-between">
        {/* Previous month button */}
        <button
          type="button"
          onClick={() => {
            if (month === 0) { setMonth(11); setYear(year - 1); }
            else setMonth(month - 1);
          }}
          className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border-2 ${themeClasses.borderSecondary} ${themeClasses.bgSurface} ${themeClasses.textPrimary} shadow-sm transition-all cursor-pointer ${
            currentTheme.isDark
              ? 'hover:border-blue-400 hover:bg-gray-700'
              : 'hover:border-quickrange hover:bg-blue-50'
          }`}
          title="Previous month"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>

        {/* Month & Year dropdowns */}
        <div className="flex gap-1 sm:gap-2">
          <select
            className={`rounded-lg px-2 sm:px-3 py-2 border-2 ${themeClasses.borderSecondary} ${themeClasses.bgSurface} ${themeClasses.textPrimary} font-semibold text-xs sm:text-sm shadow-sm transition-all cursor-pointer min-w-[90px] sm:min-w-[110px] ${
              currentTheme.isDark
                ? 'hover:border-blue-400 focus:ring-blue-400/20 focus:border-blue-400'
                : 'hover:border-quickrange hover:shadow-md focus:ring-quickrange/20 focus:border-quickrange'
            } focus:outline-none focus:ring-2`}
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
          >
            {months.map(m => (
              <option key={m} value={m}>{new Date(year, m, 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <select
            className={`rounded-lg px-2 sm:px-3 py-2 border-2 ${themeClasses.borderSecondary} ${themeClasses.bgSurface} ${themeClasses.textPrimary} font-semibold text-xs sm:text-sm shadow-sm transition-all cursor-pointer min-w-[60px] sm:min-w-[75px] ${
              currentTheme.isDark
                ? 'hover:border-blue-400 focus:ring-blue-400/20 focus:border-blue-400'
                : 'hover:border-quickrange hover:shadow-md focus:ring-quickrange/20 focus:border-quickrange'
            } focus:outline-none focus:ring-2`}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Next month button */}
        <button
          type="button"
          onClick={() => {
            if (month === 11) { setMonth(0); setYear(year + 1); }
            else setMonth(month + 1);
          }}
          className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border-2 ${themeClasses.borderSecondary} ${themeClasses.bgSurface} ${themeClasses.textPrimary} shadow-sm transition-all cursor-pointer ${
            currentTheme.isDark
              ? 'hover:border-blue-400 hover:bg-gray-700'
              : 'hover:border-quickrange hover:bg-blue-50'
          }`}
          title="Next month"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
      {/* Remove individual calendar frame, just render table */}
      <table className="w-full text-center select-none">
        <thead>
          <tr className={`text-xs ${themeClasses.textSecondary} font-bold tracking-wide`}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <th key={d} className="pb-2">{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, i) => (
            <tr key={i}>
              {week.map((date, j) => {
                const isSelected = date && selected && date.getTime() === selected.getTime();
                const isRangeStart = date && rangeStart && date.getTime() === rangeStart.getTime();
                const isRangeEnd = date && rangeEnd && date.getTime() === rangeEnd.getTime();
                const isInRange = date && rangeStart && rangeEnd && rangeStart.getTime() !== rangeEnd.getTime() && (
                  (date > rangeStart && date < rangeEnd) || (date > rangeEnd && date < rangeStart)
                );
                const isToday = date && date.getTime() === today.getTime();
                const isDisabled = !date || date < minDate || date > maxDate;
                // Check if this date has available data (use local date to avoid timezone shift)
                const hasData = date && availableDates && availableDates.has(formatLocalDate(date));
                
                // Check if this date is part of any range selection
                const isPartOfRange = isRangeStart || isRangeEnd || isInRange;
                
                let btnClass = 'w-8 h-8 sm:w-8 sm:h-8 transition-all relative z-10 text-sm sm:text-base font-medium ';
                if ((isSelected && !isRangeStart && !isRangeEnd) || (isRangeStart && isRangeEnd)) {
                  // Single date selection (start and end are same) - use button styling only
                  btnClass += currentTheme.isDark 
                    ? 'bg-blue-600 text-white font-bold rounded-full shadow-md '
                    : 'bg-[#0076b6] text-white font-bold rounded-full shadow-md ';
                } else if (isPartOfRange) {
                  // Any date that's part of a range - remove individual styling, let background handle it
                  btnClass += 'bg-transparent text-white font-bold relative z-10 ';
                } else if (isToday) {
                  btnClass += currentTheme.isDark 
                    ? 'border-2 border-yellow-400 text-yellow-400 font-bold rounded-full '
                    : 'border-2 border-yellow-400 text-yellow-700 font-bold rounded-full ';
                } else {
                  btnClass += currentTheme.isDark 
                    ? `${themeClasses.textPrimary} rounded-full `
                    : 'text-[#1a3556] rounded-full ';
                }
                if (isDisabled) btnClass += 'opacity-30 pointer-events-none ';
                else if (!isPartOfRange) {
                  btnClass += currentTheme.isDark 
                    ? 'hover:bg-gray-700 ' 
                    : 'hover:bg-[#d2e7f6] ';
                }
                
                // Create seamless background bar for the entire range
                let cellBg = '';
                const rangeBgColor = currentTheme.isDark ? 'before:bg-blue-600' : 'before:bg-[#0076b6]';
                if (isRangeStart && isRangeEnd) {
                  // Single date range - no background needed, button handles it
                  cellBg = '';
                } else if (isRangeStart && !isRangeEnd) {
                  // Start of range - rounded left, extends right
                  cellBg = `before:absolute before:inset-0 ${rangeBgColor} before:rounded-l-full before:z-0`;
                } else if (isRangeEnd && !isRangeStart) {
                  // End of range - rounded right, extends left  
                  cellBg = `before:absolute before:inset-0 ${rangeBgColor} before:rounded-r-full before:z-0`;
                } else if (isInRange) {
                  // Middle of range - no rounding
                  cellBg = `before:absolute before:inset-0 ${rangeBgColor} before:z-0`;
                }
                return (
                  <td key={j} className={`relative p-0 ${cellBg}`} style={{height: '2.5rem'}}>
                    {date ? (
                      <div className="relative w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center mx-auto">
                        <button
                          className={btnClass}
                          onClick={() => onSelect(date)}
                          disabled={isDisabled}
                          style={{zIndex: 1, minHeight: '32px', minWidth: '32px'}}
                        >
                          {date.getDate()}
                        </button>
                        {/* Data availability indicator */}
                        {hasData && !isDisabled && (
                          <div 
                            className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                              currentTheme.isDark ? 'bg-emerald-400' : 'bg-emerald-500'
                            } shadow-sm`}
                            style={{ zIndex: 2 }}
                            title="Data available"
                          />
                        )}
                      </div>
                    ) : <span className="w-8 h-8 sm:w-8 sm:h-8 inline-block" />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Interface for machine data availability
interface MachineData {
  machineId: string;
  machineName?: string;
  serialno?: string; // Optional, will use machineId as fallback
}

type DateRangePickerProps = {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string, action?: 'apply' | 'cancel') => void;
  machineData?: MachineData; // Optional machine data for showing availability indicators
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({ startDate, endDate, onChange, machineData }) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const { currentTheme } = useTheme();
  const { token } = useUnifiedAuth();
  const minDate = new Date(2000, 0, 1);
  const maxDate = new Date(2100, 11, 31);
  const [start, setStart] = React.useState<Date | null>(startDate ? parseLocalDate(startDate) : null);
  const [end, setEnd] = React.useState<Date | null>(endDate ? parseLocalDate(endDate) : null);
  const [leftMonth, setLeftMonth] = React.useState(startDate ? parseLocalDate(startDate)?.getMonth() || new Date().getMonth() : new Date().getMonth());
  const [leftYear, setLeftYear] = React.useState(startDate ? parseLocalDate(startDate)?.getFullYear() || new Date().getFullYear() : new Date().getFullYear());
  const [rightMonth, setRightMonth] = React.useState(endDate ? parseLocalDate(endDate)?.getMonth() || new Date().getMonth() : new Date().getMonth());
  const [rightYear, setRightYear] = React.useState(endDate ? parseLocalDate(endDate)?.getFullYear() || new Date().getFullYear() : new Date().getFullYear());
  // Fix: Only allow one calendar to control the range at a time, and always set start before end
  const [selecting, setSelecting] = React.useState<'start' | 'end'>('start');
  // Track if the selection has been cleared
  const [isCleared, setIsCleared] = React.useState(false);
  // State for available dates
  const [availableDates, setAvailableDates] = React.useState<Set<string>>(new Set());
  const [loadingDates, setLoadingDates] = React.useState(false);

  // Initialize calendars to show consecutive months on first load
  React.useEffect(() => {
    if (start && end) {
      const startMonth = start.getMonth();
      const startYear = start.getFullYear();
      const endMonth = end.getMonth();
      const endYear = end.getFullYear();
      
      // Set left calendar to start date's month
      setLeftMonth(startMonth);
      setLeftYear(startYear);
      
      // Set right calendar to end date's month, or next month if same
      if (startYear === endYear && startMonth === endMonth) {
        const next = new Date(startYear, startMonth + 1, 1);
        setRightMonth(next.getMonth());
        setRightYear(next.getFullYear());
      } else {
        setRightMonth(endMonth);
        setRightYear(endYear);
      }
    }
  }, []); // Only run once on mount

  // Fetch available dates when machine data changes or calendar months change
  React.useEffect(() => {
    console.log('🔍 DateRangePicker: useEffect triggered', { 
      machineData, 
      leftMonth, 
      leftYear, 
      rightMonth, 
      rightYear 
    });
    
    if (!machineData) {
      console.log('❌ DateRangePicker: No machine data provided');
      return;
    }

    const fetchAvailableDates = async () => {
      console.log('🚀 DateRangePicker: Starting to fetch available dates for machine:', machineData);
      setLoadingDates(true);
      try {
        // Calculate date range to fetch - get current visible months plus some buffer
        const startOfLeftMonth = new Date(leftYear, leftMonth, 1);
        const endOfRightMonth = new Date(rightYear, rightMonth + 1, 0); // Last day of right month
        
        // Add some buffer (1 month before and after)
        const bufferStart = new Date(startOfLeftMonth);
        bufferStart.setMonth(bufferStart.getMonth() - 1);
        const bufferEnd = new Date(endOfRightMonth);
        bufferEnd.setMonth(bufferEnd.getMonth() + 1);
        
        const dateFrom = formatLocalDate(bufferStart);
        const dateTo = formatLocalDate(bufferEnd);
        
        // Use serialno if available, otherwise use machineId
        const serialno = machineData.serialno || machineData.machineId;
        
        console.log('📤 DateRangePicker: Requesting available dates', { 
          serialno, 
          dateFrom, 
          dateTo 
        });
        
        if (serialno && token) {
          const response = await ChartDataService.fetchAvailableDates({ serialno }, token);
          if (response.success && response.data) {
            // Backend returns array of {date, exist_SAP, machine_sn} - extract just dates
            const results = Array.isArray(response.data) ? response.data : (response.data.available_dates || []);
            const dates = results.map((item: { date: string }) => item.date.split('T')[0]);
            console.log('📥 DateRangePicker: Received available dates:', dates.length);
            setAvailableDates(new Set(dates));
          }
        }
      } catch (error) {
        console.error('❌ DateRangePicker: Failed to fetch available dates:', error);
        setAvailableDates(new Set()); // Clear on error
      } finally {
        setLoadingDates(false);
      }
    };

    fetchAvailableDates();
  }, [machineData, leftMonth, leftYear, rightMonth, rightYear, token]);

  const handleDaySelect = (date: Date) => {
    // Clear the cleared state when user selects dates
    setIsCleared(false);
    
    if (selecting === 'start') {
      // First click - always set as start date
      setStart(date);
      setEnd(date); // Set end to same date initially
      setSelecting('end');
    } else {
      // Second click - determine if this should be start or end
      if (start && date.getTime() === start.getTime()) {
        // Clicked same date - keep single date selection, switch back to start mode
        setSelecting('start');
      } else if (start && date < start) {
        // Clicked earlier date - make this the new start
        setStart(date);
        setEnd(start); // Previous start becomes end
        setSelecting('start');
      } else {
        // Clicked later date - make this the end
        setEnd(date);
        setSelecting('start'); // Reset to start for next selection
      }
    }
    // Don't automatically change calendar months when selecting dates
  };

  return (
    <div className={`flex flex-col rounded-2xl shadow-lg p-3 sm:p-6 gap-4 sm:gap-6 min-w-0 sm:min-w-[700px] max-w-full border ${
      currentTheme.isDark 
        ? 'bg-gray-800 border-gray-600' 
        : 'bg-[#f5faff] border-blue-100'
    }`} style={{fontFamily: 'IBM Plex Sans, sans-serif'}}>
      <div className="text-center">
        <span className={`font-bold text-lg sm:text-xl ${
          currentTheme.isDark ? 'text-gray-100' : 'text-title'
        }`}>
          {t('selectDateRange') || 'Select Date Range'}
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
        {/* Desktop: Quick ranges sidebar on the left */}
        <div className={`hidden sm:flex sm:flex-col gap-2 sm:min-w-[180px] sm:pr-4 border-r justify-start ${
          currentTheme.isDark ? 'border-gray-600' : 'border-blue-100'
        }`}>
          {/* Show loading indicator when fetching machine data */}
          {loadingDates && machineData && (
            <div className={`text-xs px-2 py-1 rounded ${
              currentTheme.isDark ? 'text-blue-400 bg-gray-700' : 'text-blue-600 bg-blue-50'
            }`}>
              Loading data availability...
            </div>
          )}
          {/* Show machine info when available */}
          {machineData && !loadingDates && (
            <div className={`text-xs px-2 py-1 rounded ${
              currentTheme.isDark ? 'text-gray-400 bg-gray-700' : 'text-gray-600 bg-gray-50'
            }`}>
              {machineData.machineName || machineData.machineId}
            </div>
          )}
          {/* Desktop: Show quick ranges vertically */}
          {quickRanges.map(qr => (
            <button
              key={qr.label}
              className={`text-left px-2 py-2 rounded-lg font-semibold transition text-base focus:outline-none ${
                currentTheme.isDark 
                  ? 'hover:bg-gray-700 text-blue-400 focus:bg-gray-600' 
                  : 'hover:bg-cockpitblue-light/60 text-quickrange focus:bg-cockpitblue-light/80'
              }`}
              onClick={() => {
                const { startDate, endDate } = qr.range();
                setStart(startDate);
                setEnd(endDate);
                setSelecting('start'); // Reset to start mode for next manual selection
                setIsCleared(false); // Clear the cleared state
                
                // Set calendars to show the range appropriately
                setLeftMonth(startDate.getMonth());
                setLeftYear(startDate.getFullYear());
                
                // For "allTime" or very distant ranges, show current year on right calendar
                if (qr.label === 'allTime' || endDate.getFullYear() - startDate.getFullYear() > 5) {
                  setRightMonth(endDate.getMonth());
                  setRightYear(endDate.getFullYear());
                } else if (startDate.getFullYear() === endDate.getFullYear() && startDate.getMonth() === endDate.getMonth()) {
                  // Same month - show next month on right
                  const next = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
                  setRightMonth(next.getMonth());
                  setRightYear(next.getFullYear());
                } else {
                  // Normal range - show end date month on right
                  setRightMonth(endDate.getMonth());
                  setRightYear(endDate.getFullYear());
                }
              }}
            >
              {t(qr.label)}
            </button>
          ))}
        </div>

        <div className="flex-1 flex flex-col items-center">
          {/* Wrap both calendars in a single frame */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 w-full justify-center">
            {/* Mobile: Show single calendar that switches between months */}
            <div className="block sm:hidden w-full">
              <div className="flex justify-center mb-4 gap-2">
                <button
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    leftMonth === (start?.getMonth() || new Date().getMonth()) && leftYear === (start?.getFullYear() || new Date().getFullYear())
                      ? currentTheme.isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                      : currentTheme.isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => {
                    if (start) {
                      setLeftMonth(start.getMonth());
                      setLeftYear(start.getFullYear());
                    }
                  }}
                >
                  Start
                </button>
                <button
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    leftMonth === (end?.getMonth() || new Date().getMonth()) && leftYear === (end?.getFullYear() || new Date().getFullYear())
                      ? currentTheme.isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                      : currentTheme.isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => {
                    if (end) {
                      setLeftMonth(end.getMonth());
                      setLeftYear(end.getFullYear());
                    }
                  }}
                >
                  End
                </button>
              </div>
              <Calendar
                selected={selecting === 'start' ? start : end}
                onSelect={handleDaySelect}
                month={leftMonth}
                year={leftYear}
                setMonth={setLeftMonth}
                setYear={setLeftYear}
                minDate={minDate}
                maxDate={maxDate}
                rangeStart={start}
                rangeEnd={end}
                themeClasses={themeClasses}
                currentTheme={currentTheme}
                availableDates={availableDates}
              />
            </div>
            
            {/* Desktop: Show both calendars side by side */}
            <div className="hidden sm:contents">
              <Calendar
                selected={selecting === 'start' ? start : end}
                onSelect={handleDaySelect}
                month={leftMonth}
                year={leftYear}
                setMonth={setLeftMonth}
                setYear={setLeftYear}
                minDate={minDate}
                maxDate={maxDate}
                rangeStart={start}
                rangeEnd={end}
                themeClasses={themeClasses}
                currentTheme={currentTheme}
                availableDates={availableDates}
              />
              <Calendar
                selected={selecting === 'end' ? end : start}
                onSelect={handleDaySelect}
                month={rightMonth}
                year={rightYear}
                setMonth={setRightMonth}
                setYear={setRightYear}
                minDate={minDate}
                maxDate={maxDate}
                rangeStart={start}
                rangeEnd={end}
                themeClasses={themeClasses}
                currentTheme={currentTheme}
                availableDates={availableDates}
              />
            </div>
          </div>
          
          {/* Legend for data availability indicator */}
          {machineData && (
            <div className="flex items-center justify-center gap-2 mt-3 mb-1">
              <div className={`w-1.5 h-1.5 rounded-full ${
                currentTheme.isDark ? 'bg-emerald-400' : 'bg-emerald-500'
              } shadow-sm`} />
              <span className={`text-xs ${
                currentTheme.isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('dataAvailable') || 'Data available'}
              </span>
            </div>
          )}
          
          {/* Mobile only: Quick ranges section under calendar */}
          <div className={`flex sm:hidden flex-col gap-2 w-full max-w-md pt-4 mt-4 border-t justify-center ${
            currentTheme.isDark ? 'border-gray-600' : 'border-blue-100'
          }`}>
            {/* Show loading indicator when fetching machine data */}
            {loadingDates && machineData && (
              <div className={`text-xs px-2 py-1 rounded text-center ${
                currentTheme.isDark ? 'text-blue-400 bg-gray-700' : 'text-blue-600 bg-blue-50'
              }`}>
                Loading data availability...
              </div>
            )}
            {/* Show machine info when available */}
            {machineData && !loadingDates && (
              <div className={`text-xs px-2 py-1 rounded text-center ${
                currentTheme.isDark ? 'text-gray-400 bg-gray-700' : 'text-gray-600 bg-gray-50'
              }`}>
                {machineData.machineName || machineData.machineId}
              </div>
            )}
            {/* Mobile: Show quick ranges in a wrapping grid */}
            <div className="flex flex-wrap gap-2 justify-center">
              {quickRanges.map(qr => (
                <button
                  key={qr.label}
                  className={`text-center px-2 py-1.5 rounded-lg font-medium transition text-xs focus:outline-none flex-shrink-0 ${
                    currentTheme.isDark 
                      ? 'hover:bg-gray-700 text-blue-400 focus:bg-gray-600' 
                      : 'hover:bg-cockpitblue-light/60 text-quickrange focus:bg-cockpitblue-light/80'
                  }`}
                  onClick={() => {
                    const { startDate, endDate } = qr.range();
                    setStart(startDate);
                    setEnd(endDate);
                    setSelecting('start'); // Reset to start mode for next manual selection
                    setIsCleared(false); // Clear the cleared state
                    
                    // Set calendars to show the range appropriately
                    setLeftMonth(startDate.getMonth());
                    setLeftYear(startDate.getFullYear());
                    
                    // For "allTime" or very distant ranges, show current year on right calendar
                    if (qr.label === 'allTime' || endDate.getFullYear() - startDate.getFullYear() > 5) {
                      setRightMonth(endDate.getMonth());
                      setRightYear(endDate.getFullYear());
                    } else if (startDate.getFullYear() === endDate.getFullYear() && startDate.getMonth() === endDate.getMonth()) {
                      // Same month - show next month on right
                      const next = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
                      setRightMonth(next.getMonth());
                      setRightYear(next.getFullYear());
                    } else {
                      // Normal range - show end date month on right
                      setRightMonth(endDate.getMonth());
                      setRightYear(endDate.getFullYear());
                    }
                  }}
                >
                  {t(qr.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-center mt-4">
            <button
              className={`font-medium px-3 py-2 sm:py-1 rounded transition text-sm w-full sm:w-auto ${
                (!start && !end) 
                  ? currentTheme.isDark ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                  : currentTheme.isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
              onClick={() => {
                setStart(null);
                setEnd(null);
                setIsCleared(true);
                setSelecting('start');
              }}
              disabled={!start && !end}
            >
              {t('clearSelection') || 'Clear Selection'}
            </button>
            <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                className={`font-semibold px-3 sm:px-4 py-2 rounded-lg transition flex-1 sm:flex-none text-sm sm:text-base ${
                  currentTheme.isDark 
                    ? 'text-blue-400 hover:bg-gray-700' 
                    : 'text-quickrange hover:bg-cockpitblue-light/60'
                }`}
                onClick={() => onChange(startDate, endDate, 'cancel')}
              >
                {t('cancel')}
              </button>
              <button
                className={`font-bold px-3 sm:px-6 py-2 rounded-lg shadow transition flex-1 sm:flex-none text-sm sm:text-base ${
                  (isCleared || (start && end))
                    ? currentTheme.isDark 
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (isCleared) {
                    // Apply cleared selection (empty date range)
                    onChange('', '', 'apply');
                  } else if (start && end) {
                    // Apply normal date range using timezone-safe formatting
                    onChange(
                      formatLocalDate(start),
                      formatLocalDate(end),
                      'apply'
                    );
                  }
                }}
                disabled={!isCleared && (!start || !end)}
              >
                {t('apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
